import { IContextualMenuProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPDefaultButton, IPLabel, IPSeparator } from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import HrefLink from 'components/Links/HrefLink';
import TextInfo from 'components/Notification/Info';
import InfoMessage from 'components/Notification/InfoMessage';
import MultiSelectUserPicker from 'components/Pickers/MultiSelectUserPicker';
import {
  deleteIcon,
  eventIcon,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  templateIcon,
} from 'globalStyles';
import { ApprovalMaxUsers } from 'models/approval';
import Task from 'models/tasks/task';
import { SystemTaskTypes } from 'models/tasks/taskType';
import User from 'models/user';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWebhooksForType } from 'services/Api/webhookService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { sortOnString } from 'utils/sorting';
import { applyApprovalOwners, copyFromTemplate, setApprovalApprovers } from './ApprovalHelpers';
import { INewApproval } from './CreateApproval';

interface IEditNewApprovalModalProps {
  selectedApprovals: INewApproval[];
  isOpen: boolean;
  saveButtonText?: string;
  onClose: () => void;
  onSave: (newApprovals: INewApproval[]) => void;
}

export const EditNewApprovalModal = (props: IEditNewApprovalModalProps) => {
  const { t } = useTranslation(['library', 'translation', 'task']);
  const appContext = useContext(AppContext);
  const [templates, setTemplates] = useState<Task[]>([]);
  const [showWorkflowMsg, setShowWorkflowMsg] = useState<boolean>(false);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Task | undefined>(undefined);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [addOwners, setAddOwners] = useState<boolean>(false);

  useEffect(() => {
    loadApprovalTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      const userIds = selectedTemplate.instances?.filter((i) => i.userId !== undefined).map((i) => i.userId as string);
      const users = appContext.globalDataCache.users.getItemsForId(userIds);

      //add owners
      if (selectedTemplate.instances?.some((i) => i.userId === undefined)) {
        setAddOwners(true);
      }

      setSelectedUsers(users);
      checkIfWorkflow(selectedTemplate);
    } else {
      setSelectedUsers([]);
      setAddOwners(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const checkIfWorkflow = async (template: Task) => {
    //check if this template has connected webhooks (which trigger a workflow)
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const webHooks = await apiGetWebhooksForType('task', template.taskId, accessToken);
    setShowWorkflowMsg(webHooks.length > 0);
  };

  const loadApprovalTemplates = async () => {
    try {
      if (templatesLoading) return;
      setTemplatesLoading(true);

      const _templates = await appContext.globalDataCache.templates.getItems();
      const approvalTemplates = _templates
        .filter((t) => t.systemTaskType === SystemTaskTypes.Approval)
        .sort((a, b) => sortOnString(a.name, b.name));

      setTemplates(approvalTemplates);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const getApprovalTemplates: IContextualMenuProps = useMemo(() => {
    const output: IContextualMenuProps = {
      items: [],
      directionalHint: IPDirectionalHint.bottomLeftEdge,
      directionalHintFixed: true,
      calloutProps: {
        calloutMaxHeight: 400,
      },
    };

    if (templates && templates.length > 0) {
      output.items = templates.map((_itm: Task) => {
        return {
          key: `${_itm.taskId}`,
          text: `${_itm.name}`,
          canCheck: true,
          checked: selectedTemplate?.taskId === _itm.taskId,
          iconProps: eventIcon,
          onClick: () => {
            setSelectedTemplate(_itm);
          },
        };
      });
    }

    if (selectedTemplate && output.items.length > 0) {
      output.items.push(
        {
          key: '-',
          itemType: IPContextualMenuItemType.Divider,
        },
        {
          key: `-2`,
          text: t('library:ApprovalTasks.CreateNewTab.RemoveTemplate'),
          iconProps: deleteIcon,
          onClick: () => {
            setSelectedTemplate(undefined);
            setShowWorkflowMsg(false);
          },
        },
      );
    }

    if (output.items.length === 0) {
      output.items = [
        {
          key: '-1',
          onRender: () => {
            return (
              <IPStack styles={{ root: { padding: 5 } }}>
                <HrefLink href={'/admin/content/templates'}>
                  {t('library:ApprovalTasks.CreateNewTab.TemplatesEmpty')}
                </HrefLink>
              </IPStack>
            );
          },
        },
      ];
    }

    return output;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, templatesLoading, selectedTemplate]);

  const save = async () => {
    try {
      const newApprovals: INewApproval[] = [];

      for (let idx = 0; idx < props.selectedApprovals.length; idx++) {
        const approval = props.selectedApprovals[idx];
        approval.task.instances = [];

        if (selectedTemplate) {
          //apply the template
          approval.task = copyFromTemplate(selectedTemplate, approval.task, appContext);
        } else {
          //apply the users
          setApprovalApprovers(approval.task, selectedUsers);
        }

        //apply the owners on top, when the editor asked for them
        if (addOwners) {
          await applyApprovalOwners(approval, appContext);
        }

        approval.task.instances = approval.task.instances?.slice(0, ApprovalMaxUsers);

        //every edited approval is saved: leaving this inside the addOwners branch dropped the
        //approvers the editor had just picked whenever the owners were not asked for
        newApprovals.push(approval);
      }

      props.onSave(newApprovals);
    } catch (err) {
      appContext.setError(err);
    }
  };

  //
  // Main render
  //
  return (
    <GenericModal
      title={t('library:ApprovalTasks.Dialogs.EditNew.Title')}
      subTitle={t('library:ApprovalTasks.Dialogs.EditNew.SubTitle', { count: props.selectedApprovals.length })}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSave={save}
      saveButtonText={props.saveButtonText}
      saveDisabled={!showWorkflowMsg && selectedUsers.length === 0 && !addOwners}
      width={'40vw'}
      minWidth={250}
      maxWidth={'95vw'}
      hideFooter={false}
    >
      <IPSeparator />
      <IPStack tokens={globalStackTokensGapMedium}>
        {showWorkflowMsg && <InfoMessage message={t('library:ApprovalTasks.CreateNewTab.TemplateWorkflowInfo')} />}
        <IPStack.Item>
          <TextInfo
            message={t('library:ApprovalTasks.Dialogs.EditNew.UserSelectLabelInfo', { count: ApprovalMaxUsers })}
          >
            <IPLabel>{t('library:ApprovalTasks.Dialogs.EditNew.UserSelectLabel')}</IPLabel>
          </TextInfo>
          <IPStack horizontal verticalAlign="start" tokens={globalStackTokensGapSmall}>
            <IPStack.Item grow>
              <IPStack tokens={globalStackTokensGapSmall}>
                <MultiSelectUserPicker
                  users={undefined}
                  itemLimit={ApprovalMaxUsers}
                  disabled={selectedTemplate && selectedTemplate.instancesEnabled !== true}
                  selectedUsers={selectedUsers ?? []}
                  onMultipleSelect={(items: User[] | undefined) => {
                    if (items && items.length > 0) {
                      setSelectedUsers([...items]);
                    } else {
                      setSelectedUsers([]);
                    }
                  }}
                />
                <IPCheckbox
                  checked={addOwners}
                  disabled={selectedTemplate && selectedTemplate.instancesEnabled !== true}
                  label={t('task:TaskTypes.ApprovalTemplate.IncludeOwners')}
                  onChange={(ev, checked) => {
                    setAddOwners(checked ?? false);
                  }}
                />
              </IPStack>
            </IPStack.Item>
            {hasUserFeature(appContext, FeatureTypes.Events) && (
              <IPStack.Item>
                <IPDefaultButton
                  iconProps={templateIcon}
                  disabled={templatesLoading}
                  text={t('task:TopTasks.Template')}
                  menuProps={getApprovalTemplates}
                />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};
